import { Button } from '@basqet-dashboard/common/components';
import { ANALYTICS_EVENTS, KYB_ACCOUNT_TYPE } from '@config';
import { useAnalytics, useTrackOnMount } from '@hooks';
import { useCompliance } from '@hooks/useCompliance';
import { mapDueDiligence } from '@utils/compliance-mappers';
import PropTypes from 'prop-types';
import React, { useCallback, useReducer, useRef } from 'react';
import { toast } from 'react-toastify';

// ─── Constants ────────────────────────────────────────────────────────────────

const TOTAL_PAGES = 4;

const YNA = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'na', label: 'N/A' }
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const initFormData = (data) => ({
  // Page 1 — General AML
  has_aml_program: data?.has_aml_program || '',
  has_compliance_officer: data?.has_compliance_officer || '',
  compliance_officer_name: data?.compliance_officer_name || '',
  compliance_officer_designation: data?.compliance_officer_designation || '',
  compliance_officer_email: data?.compliance_officer_email || '',
  compliance_officer_phone: data?.compliance_officer_phone || '',
  has_documented_policies: data?.has_documented_policies || '',
  has_internal_audit: data?.has_internal_audit || '',
  has_enforcement_actions: data?.has_enforcement_actions || '',
  enforcement_details: data?.enforcement_details || '',
  // Page 2 — KYC / Due Diligence
  has_kyc_policy: data?.has_kyc_policy || '',
  has_identification_procedures: data?.has_identification_procedures || '',
  has_record_retention: data?.has_record_retention || '',
  retention_period: data?.retention_period || '',
  has_ofac_screening: data?.has_ofac_screening || '',
  has_customer_updates: data?.has_customer_updates || '',
  update_frequency: data?.update_frequency || '',
  // Page 3 — SAR + AML Training
  has_sar_policies: data?.has_sar_policies || '',
  has_aml_training: data?.has_aml_training || '',
  has_training_records: data?.has_training_records || '',
  has_aml_updates: data?.has_aml_updates || '',
  provides_third_party_training: data?.provides_third_party_training || '',
  // Page 4 — Anti-Bribery / Anti-Corruption
  has_abc_policy: data?.has_abc_policy || '',
  has_govt_official: data?.has_govt_official || '',
  govt_official_name: data?.govt_official_name || '',
  govt_official_association: data?.govt_official_association || '',
  has_gifts_policy: data?.has_gifts_policy || '',
  has_political_contributions: data?.has_political_contributions || '',
  has_abc_reporting_system: data?.has_abc_reporting_system || '',
  has_abc_training: data?.has_abc_training || '',
  certified: data?.certified || false
});

const validatePage = (page, d) => {
  const e = {};
  const req = (k) => {
    if (!d[k]) e[k] = 'Please select an option';
  };
  const reqText = (k, msg) => {
    if (!String(d[k] || '').trim()) e[k] = msg || 'Required';
  };

  if (page === 0) {
    req('has_aml_program');
    req('has_compliance_officer');
    if (d.has_compliance_officer === 'yes') {
      reqText('compliance_officer_name', 'Name is required');
      reqText('compliance_officer_designation', 'Designation is required');
      reqText('compliance_officer_email', 'Email is required');
      reqText('compliance_officer_phone', 'Phone number is required');
    }
    req('has_documented_policies');
    req('has_internal_audit');
    req('has_enforcement_actions');
    if (d.has_enforcement_actions === 'yes')
      reqText('enforcement_details', 'Please provide details');
  }

  if (page === 1) {
    req('has_kyc_policy');
    req('has_identification_procedures');
    req('has_record_retention');
    if (d.has_record_retention === 'yes')
      reqText('retention_period', 'Please enter the retention period');
    req('has_ofac_screening');
    req('has_customer_updates');
    if (d.has_customer_updates === 'yes') reqText('update_frequency', 'Please enter the period');
  }

  if (page === 2) {
    req('has_sar_policies');
    req('has_aml_training');
    req('has_training_records');
    req('has_aml_updates');
    req('provides_third_party_training');
  }

  if (page === 3) {
    req('has_abc_policy');
    req('has_govt_official');
    if (d.has_govt_official === 'yes') {
      reqText('govt_official_name', 'Name is required');
      reqText('govt_official_association', 'Association is required');
    }
    req('has_gifts_policy');
    req('has_political_contributions');
    req('has_abc_reporting_system');
    req('has_abc_training');
    if (!d.certified) e.certified = 'You must certify before submitting';
  }

  return e;
};

// ─── Reusable sub-components (stable references — defined outside render) ────

const RadioGroup = React.memo(({ field, value, onChange, error }) => (
  <div>
    <div className="ddq__radio-group">
      {YNA.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`ddq__radio-btn${value === opt.value ? ' ddq__radio-btn--selected' : ''}`}
          onClick={() => onChange(field, opt.value)}
          aria-pressed={value === opt.value}>
          <span className="ddq__radio-circle">
            {value === opt.value && <span className="ddq__radio-dot" />}
          </span>
          {opt.label}
        </button>
      ))}
    </div>
    {error && <span className="ddq__field-error">{error}</span>}
  </div>
));

RadioGroup.displayName = 'RadioGroup';
RadioGroup.propTypes = {
  field: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string
};

const ConditionalBox = ({ show, label, children }) => {
  if (!show) return null;
  return (
    <div className="ddq__conditional">
      {label && <p className="ddq__conditional-label">* {label}</p>}
      {children}
    </div>
  );
};

ConditionalBox.propTypes = {
  show: PropTypes.bool.isRequired,
  label: PropTypes.string,
  children: PropTypes.node.isRequired
};

const Question = ({ text, children }) => (
  <div className="ddq__question">
    <p className="ddq__question-text">{text}</p>
    {children}
  </div>
);

Question.propTypes = {
  text: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

const TextField = ({ name, label, placeholder, value, onChange, error, multiline }) => (
  <div className="ddq__text-field">
    {label && <p className="ddq__text-label">{label}</p>}
    {multiline ? (
      <textarea
        className="ddq__textarea"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        rows={3}
      />
    ) : (
      <input
        className="ddq__input"
        type="text"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        autoComplete="off"
      />
    )}
    {error && <span className="ddq__field-error">{error}</span>}
  </div>
);

TextField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  multiline: PropTypes.bool
};

const TwoColGrid = ({ children }) => <div className="ddq__grid-2">{children}</div>;

TwoColGrid.propTypes = { children: PropTypes.node.isRequired };

// ─── State reducer ────────────────────────────────────────────────────────────

const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_ERRORS':
      return { ...state, _errors: action.errors };
    default:
      return state;
  }
};

// ─── Main component ───────────────────────────────────────────────────────────

const DueDiligenceQuestionnaire = ({ onComplete, initialData, onSaveDraft, readOnly = false }) => {
  const { track } = useAnalytics();
  const { saveSection, submitForReview } = useCompliance();
  const [currentPage, setCurrentPage] = React.useState(initialData?._currentPage ?? 0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const containerRef = useRef(null);

  // initialData is only read once on mount — useReducer's initializer arg handles this cleanly
  const [state, dispatch] = useReducer(formReducer, initialData, initFormData);

  const errors = state._errors || {};
  const d = state; // shorthand

  useTrackOnMount(ANALYTICS_EVENTS.KYB_DD_VIEWED, {
    kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
  });

  const handleChange = useCallback((field, value) => {
    dispatch({ type: 'SET_FIELD', field, value });
    // Clear error for this field on change
    dispatch({ type: 'SET_ERRORS', errors: {} });
  }, []);

  const scrollToTop = useCallback(() => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);

      track(ANALYTICS_EVENTS.CFI_DD_SAVED, { kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI });

      // Strip internal _errors key before mapping
      // eslint-disable-next-line no-unused-vars
      const { _errors, ...formData } = d;
      const apiData = mapDueDiligence(formData);

      const result = await saveSection('DUE_DILIGENCE', apiData, {
        entryId: initialData?._entryId,
        version: initialData?._version
      });

      // Submit for review (this is always the last step); pass idempotency key
      await submitForReview(crypto.randomUUID());

      toast.success('Due Diligence Questionnaire submitted successfully');
      onComplete({ ...formData, _entryId: result.entry_id, _version: result.version });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save due diligence:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [d, track, saveSection, submitForReview, onComplete, initialData]);

  const handleNext = useCallback(() => {
    const pageErrors = validatePage(currentPage, d);
    if (Object.keys(pageErrors).length > 0) {
      dispatch({ type: 'SET_ERRORS', errors: pageErrors });
      return;
    }
    if (currentPage < TOTAL_PAGES - 1) {
      track(ANALYTICS_EVENTS.CFI_DD_INFO_ADDED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
        page: currentPage + 1
      });
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      onSaveDraft?.({ _currentPage: nextPage });
      scrollToTop();
    } else {
      // Last page — submit
      handleSubmit();
    }
  }, [currentPage, d, onSaveDraft, track, handleSubmit, scrollToTop]);

  const handlePrev = useCallback(() => {
    if (currentPage > 0) {
      dispatch({ type: 'SET_ERRORS', errors: {} });
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      onSaveDraft?.({ _currentPage: prevPage });
      scrollToTop();
    }
  }, [currentPage, scrollToTop, onSaveDraft]);

  const r = useCallback(
    (field) => ({
      field,
      value: d[field] || '',
      onChange: handleChange,
      error: errors[field]
    }),
    [d, handleChange, errors]
  );

  // ─── Pages ─────────────────────────────────────────────────────────────────

  const pages = [
    // Page 1 — General AML Policies, Procedures
    <div key="p1" className="ddq__section">
      <div className="ddq__section-header">General AML Policies, Procedures</div>

      <Question text="Does your company have a written AML Program?">
        <RadioGroup {...r('has_aml_program')} />
      </Question>

      <Question text="Does your company have a Compliance Program that includes a designated Compliance Officer?">
        <RadioGroup {...r('has_compliance_officer')} />
        <ConditionalBox
          show={d.has_compliance_officer === 'yes'}
          label="If yes, please provide Compliance Officer's Details:">
          <TwoColGrid>
            <TextField
              name="compliance_officer_name"
              label="Name"
              placeholder="First name, Last name"
              value={d.compliance_officer_name}
              onChange={handleChange}
              error={errors.compliance_officer_name}
            />
            <TextField
              name="compliance_officer_designation"
              label="Designation"
              placeholder="Enter designation"
              value={d.compliance_officer_designation}
              onChange={handleChange}
              error={errors.compliance_officer_designation}
            />
          </TwoColGrid>
          <TwoColGrid>
            <TextField
              name="compliance_officer_email"
              label="Email"
              placeholder="Enter email address"
              value={d.compliance_officer_email}
              onChange={handleChange}
              error={errors.compliance_officer_email}
            />
            <TextField
              name="compliance_officer_phone"
              label="Phone Number"
              placeholder="Enter phone number"
              value={d.compliance_officer_phone}
              onChange={handleChange}
              error={errors.compliance_officer_phone}
            />
          </TwoColGrid>
        </ConditionalBox>
      </Question>

      <Question text="Has your company developed written policies documenting the processes that it has in place to prevent, detect, and report suspicious transactions?">
        <RadioGroup {...r('has_documented_policies')} />
      </Question>

      <Question text="Does your company have an internal audit function or other independent Third-Party that assesses the adequacy of AML procedures and policies on a regular basis?">
        <RadioGroup {...r('has_internal_audit')} />
      </Question>

      <Question text="Has your company had any regulatory or criminal enforcement actions resulting from violations of AML laws or regulations in the last five (5) years?">
        <RadioGroup {...r('has_enforcement_actions')} />
        <ConditionalBox
          show={d.has_enforcement_actions === 'yes'}
          label="If yes, please provide details?">
          <TextField
            name="enforcement_details"
            placeholder="More details"
            value={d.enforcement_details}
            onChange={handleChange}
            error={errors.enforcement_details}
            multiline
          />
        </ConditionalBox>
      </Question>
    </div>,

    // Page 2 — KYC and Due Diligence
    <div key="p2" className="ddq__section">
      <div className="ddq__section-header">Know-Your-Customer (KYC) and Due Diligence</div>

      <Question text="Does your company have a written KYC/Due Diligence Policy?">
        <RadioGroup {...r('has_kyc_policy')} />
      </Question>

      <Question text="Have you implemented procedures for identifying your business partners/customers and do you maintain records of your customers?">
        <RadioGroup {...r('has_identification_procedures')} />
      </Question>

      <Question text="Does your company have a record retention procedure that complies with applicable laws?">
        <RadioGroup {...r('has_record_retention')} />
        <ConditionalBox
          show={d.has_record_retention === 'yes'}
          label="If yes, what is the retention period?">
          <TextField
            name="retention_period"
            placeholder="Enter retention period"
            value={d.retention_period}
            onChange={handleChange}
            error={errors.retention_period}
          />
        </ConditionalBox>
      </Question>

      <Question text="Does your company screen transactions against the US Office of Foreign Assets Control (OFAC) and other sanction lists?">
        <RadioGroup {...r('has_ofac_screening')} />
      </Question>

      <Question text="Does your company regularly update customer/partner information?">
        <RadioGroup {...r('has_customer_updates')} />
        <ConditionalBox show={d.has_customer_updates === 'yes'} label="What is the period?">
          <TextField
            name="update_frequency"
            placeholder="Enter period"
            value={d.update_frequency}
            onChange={handleChange}
            error={errors.update_frequency}
          />
        </ConditionalBox>
      </Question>
    </div>,

    // Page 3 — Suspicious Activity Reporting + AML Training
    <div key="p3">
      <div className="ddq__section">
        <div className="ddq__section-header">Suspicious Activity Reporting</div>

        <Question text="Does the company have policies or practices for the identification and reporting of transactions/activities that appear suspicious?">
          <RadioGroup {...r('has_sar_policies')} />
        </Question>
      </div>

      <div className="ddq__section">
        <div className="ddq__section-header">AML Training</div>

        <Question text="Do you provide AML training to employees?">
          <RadioGroup {...r('has_aml_training')} />
        </Question>

        <Question text="If yes, do you retain records of training sessions including attendance records and relevant training materials used?">
          <RadioGroup {...r('has_training_records')} />
        </Question>

        <Question text="Do you communicate new AML related laws or changes to existing AML related policies or procedures to employees?">
          <RadioGroup {...r('has_aml_updates')} />
        </Question>

        <Question text="Does the company provide AML training to relevant third parties?">
          <RadioGroup {...r('provides_third_party_training')} />
        </Question>
      </div>
    </div>,

    // Page 4 — Anti-Bribery and Anti-Corruption Declarations
    <div key="p4" className="ddq__section">
      <div className="ddq__section-header">Anti-Bribery and Anti-Corruption Declarations</div>

      <Question text="Does your company have an Anti-Bribery and Anti-Corruption Policy?">
        <RadioGroup {...r('has_abc_policy')} />
      </Question>

      <Question text='Is anyone within your company&apos;s management, employees, Board of Directors, subsidiaries, or affiliates a "government official"?'>
        <RadioGroup {...r('has_govt_official')} />
        <ConditionalBox
          show={d.has_govt_official === 'yes'}
          label="If yes, please indicate the name of the person and describe their association with your company.">
          <TwoColGrid>
            <TextField
              name="govt_official_name"
              label="Name"
              placeholder="First name, Last name"
              value={d.govt_official_name}
              onChange={handleChange}
              error={errors.govt_official_name}
            />
            <TextField
              name="govt_official_association"
              label="Association with Company"
              placeholder="State association with company"
              value={d.govt_official_association}
              onChange={handleChange}
              error={errors.govt_official_association}
            />
          </TwoColGrid>
        </ConditionalBox>
      </Question>

      <Question text="Does your company have a written policy related to the giving and receiving of gifts?">
        <RadioGroup {...r('has_gifts_policy')} />
      </Question>

      <Question text="Does your company contribute to the campaigns of politicians, political candidates, or government office holders?">
        <RadioGroup {...r('has_political_contributions')} />
      </Question>

      <Question text="Does your company have a system for reporting anti-bribery and anti-corruption violations?">
        <RadioGroup {...r('has_abc_reporting_system')} />
      </Question>

      <Question text="Does the Entity provide ABC training that is targeted to specific roles, responsibilities, and activities?">
        <RadioGroup {...r('has_abc_training')} />
      </Question>

      <label className="ddq__certify">
        <input
          type="checkbox"
          className="ddq__certify-checkbox"
          checked={d.certified}
          onChange={(e) => handleChange('certified', e.target.checked)}
        />
        I certify that the information provided is true and complete, and that I am duly authorized
        to submit this on behalf of the business.
      </label>
      {errors.certified && <span className="ddq__field-error">{errors.certified}</span>}
    </div>
  ];

  const isLastPage = currentPage === TOTAL_PAGES - 1;

  return (
    <div className="ddq" ref={containerRef}>
      <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
        {pages[currentPage]}
      </fieldset>

      {!readOnly && isLastPage && (
        <Button isFull onClick={handleNext} isLoading={isSubmitting} disabled={isSubmitting}>
          Save and Continue
        </Button>
      )}

      <div className="ddq__nav">
        <button
          type="button"
          className="ddq__nav-btn ddq__nav-btn--prev"
          onClick={handlePrev}
          disabled={currentPage === 0}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Previous
        </button>

        <span className="ddq__nav-page">
          Page {currentPage + 1} of {TOTAL_PAGES}
        </span>

        <button
          type="button"
          className="ddq__nav-btn ddq__nav-btn--next"
          onClick={!isLastPage ? handleNext : undefined}
          disabled={isLastPage}>
          Next
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 18l6-6-6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

DueDiligenceQuestionnaire.propTypes = {
  onComplete: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  onSaveDraft: PropTypes.func,
  readOnly: PropTypes.bool
};

export { DueDiligenceQuestionnaire };
